import React from 'react';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import './VenueRestaurantTile.css';

export interface VenueRestaurantTileData {
  id: string;
  name: string;
  slug: string;
  coverImageUrl: string | null;
  cuisine: string | null;
  subscriptionTier: 'FREE' | 'PRO' | 'PREMIUM';
  openNow: boolean;
  opensAt: string | null;
  rating: number | null;
}

interface Props {
  restaurant: VenueRestaurantTileData;
  onClick: () => void;
}

/**
 * Square cover tile for the 2-col grid (non-featured + closed restaurants).
 *  - 1:1 cover with subtle bottom gradient
 *  - Tier label top-left ("PRO" / "FREE")
 *  - Status dot top-right (green = open, neutral = closed)
 *  - Rating bottom-left over photo
 *  - Name + cuisine + open/closed text below
 */
function VenueRestaurantTile({ restaurant: r, onClick }: Props) {
  const { t } = useTranslation();
  const cuisineKey = r.cuisine ?? '';
  const cuisineLabel = cuisineKey
    ? t(`cuisine.${cuisineKey}`, { defaultValue: cuisineKey })
    : '';
  const statusText = r.openNow
    ? t('venueLanding.openShort', { defaultValue: 'Открыт' })
    : r.opensAt
      ? t('venueLanding.opensAtShort', {
          time: r.opensAt,
          defaultValue: `Откр. в ${r.opensAt}`,
        })
      : t('venueLanding.closed');
  const ariaLabel = t('venueLanding.cardAriaLabel', {
    name: r.name,
    cuisine: cuisineLabel,
    status: statusText,
  });

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={`vl-tile ${r.openNow ? '' : 'vl-tile--closed'}`}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel}
      data-slug={r.slug}
      data-id={r.id}
    >
      <div className="vl-tile-cover">
        {r.coverImageUrl ? (
          <img src={r.coverImageUrl} alt="" />
        ) : (
          <div className="vl-tile-cover-fallback" aria-hidden="true">
            {r.name.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="vl-tile-cover-grad" aria-hidden="true" />

        {r.subscriptionTier !== 'FREE' && (
          <div className="vl-tile-tier">{r.subscriptionTier}</div>
        )}

        <div
          className={`vl-tile-status ${r.openNow ? 'vl-tile-status--open' : 'vl-tile-status--closed'}`}
          aria-hidden="true"
        />

        {r.rating != null && (
          <div className="vl-tile-rating">
            <Star size={10} fill="#fff" stroke="#fff" aria-hidden="true" />
            <span>{r.rating.toFixed(1)}</span>
          </div>
        )}
      </div>

      <div className="vl-tile-body">
        <div className="vl-tile-name">{r.name}</div>
        <div className="vl-tile-meta">
          <span
            className={`vl-tile-status-text ${r.openNow ? 'vl-tile-status-text--open' : 'vl-tile-status-text--closed'}`}
          >
            {statusText}
          </span>
          {cuisineLabel && (
            <>
              <span className="vl-tile-meta-dot" aria-hidden="true" />
              <span>{cuisineLabel}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default VenueRestaurantTile;
